/**
 * Round 98n (Jul 2026) — IPO Alert popup modal.
 *
 * Fires once per calendar day (ET) on the Portfolio / Dashboard page
 * when the user has at least one undismissed IPO alert matching their
 * `/ipo-watch` alert settings.
 *
 * Mirrors ExDivAlertModal.jsx architecture but scoped to new IPOs:
 *   1. On mount, fetches /api/ipo-alerts/upcoming after a 3-second
 *      delay (same "settle-in" cadence as the ex-div popup).
 *   2. If `count > 0` AND `prefs.popup_dismissed_until` isn't today or
 *      later, opens a modal listing every unread IPO.
 *   3. Per-row DISMISS button POSTs to /api/ipo-alerts/dismiss/{ticker}
 *      — that ticker won't reappear in the bell OR in this modal until
 *      the user changes any filter on /ipo-watch (server-enforced).
 *   4. Master "Not today" button suppresses the modal for the rest of
 *      the ET calendar day via /api/ipo-alerts/popup-dismiss.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Rocket, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_TONE = {
  REIT: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-400/50',
  CEF: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-400/50',
  BDC: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-400/50',
  PFD: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-400/50',
  MLP: 'bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-400/50',
  SPAC: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-400/50',
  OTHER: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-400/50',
};

function todayEtIso() {
  // ET date approximation — same trick used elsewhere in this codebase
  // (see server.py:25320). UTC is close enough for a daily gate.
  return new Date().toISOString().slice(0, 10);
}

export default function IpoAlertModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [hiddenTickers, setHiddenTickers] = useState(() => new Set());

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ipo-alerts/upcoming`, { withCredentials: true });
      const list = Array.isArray(data?.alerts) ? data.alerts : [];
      const prefs = data?.prefs || {};
      const dismissedUntil = prefs.popup_dismissed_until || null;
      const suppressToday = dismissedUntil && dismissedUntil >= todayEtIso();
      setAlerts(list);
      if (!suppressToday && list.length > 0 && prefs.enabled) {
        setOpen(true);
      }
    } catch {
      // Silent — modal is a passive nudge, no need to bother the user.
    }
  }, []);

  useEffect(() => {
    // Same 3-second settle-in as the ex-div modal so we don't stack
    // popups on Dashboard load.
    const t = setTimeout(fetchAlerts, 3000);
    return () => clearTimeout(t);
  }, [fetchAlerts]);

  const visible = alerts.filter((a) => !hiddenTickers.has(a.ticker));

  const dismissRow = async (ticker) => {
    setHiddenTickers((prev) => {
      const next = new Set(prev);
      next.add(ticker);
      return next;
    });
    try {
      await axios.post(`${API}/ipo-alerts/dismiss/${encodeURIComponent(ticker)}`, {}, { withCredentials: true });
    } catch {
      /* silent — worst case the alert re-appears next reload */
    }
  };

  const dismissAll = async () => {
    setOpen(false);
    try {
      await axios.post(`${API}/ipo-alerts/popup-dismiss`, { days: 1 }, { withCredentials: true });
    } catch {
      /* silent */
    }
  };

  const openStock = (ticker) => {
    setOpen(false);
    navigate(`/stock/${ticker}`);
  };

  const goToIpoWatch = () => {
    setOpen(false);
    navigate('/ipo-watch');
  };

  if (!open || visible.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ipo-alert-modal"
    >
      <div className="relative w-full max-w-lg rounded-2xl border-2 border-violet-500/40 bg-card shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border bg-violet-500/5">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            <div>
              <h2 className="text-sm font-bold tracking-tight" data-testid="ipo-modal-title">
                New IPOs in your window ({visible.length})
              </h2>
              <p className="text-[10px] text-muted-foreground font-semibold">
                Adjust your filters on the IPO Watch page.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1 rounded-md hover:bg-muted/60 text-muted-foreground"
            aria-label="Close"
            data-testid="ipo-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Alert list */}
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-border" data-testid="ipo-modal-list">
          {visible.map((a) => {
            const tone = KIND_TONE[a.kind] || KIND_TONE.OTHER;
            return (
              <div
                key={a.ticker}
                className="flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors"
                data-testid={`ipo-modal-row-${a.ticker}`}
              >
                <button
                  type="button"
                  onClick={() => openStock(a.ticker)}
                  className="font-mono font-bold text-sm text-violet-700 dark:text-violet-300 hover:underline"
                >
                  {a.ticker}
                </button>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${tone}`}>
                  {a.kind}
                </span>
                {a.is_upcoming && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                    title="Not yet listed"
                    data-testid={`ipo-modal-upcoming-${a.ticker}`}
                  >
                    UPCOMING
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-foreground truncate" title={a.issuer_name || ''}>
                    {a.issuer_name || '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Listed {a.listing_date || '—'}
                    {a.primary_exchange ? ` · ${a.primary_exchange}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => dismissRow(a.ticker)}
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-rose-300/60 dark:border-rose-800/60 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10"
                  title="Dismiss this IPO. Change your window setting to re-surface."
                  data-testid={`ipo-modal-dismiss-${a.ticker}`}
                >
                  DISMISS
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border bg-muted/20">
          <button
            type="button"
            onClick={goToIpoWatch}
            className="text-[11px] font-bold px-3 py-1.5 rounded-md text-violet-700 dark:text-violet-300 hover:bg-violet-500/10 flex items-center gap-1"
            data-testid="ipo-modal-see-all"
          >
            See all on IPO Watch
            <ExternalLink className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={dismissAll}
            className="text-[11px] font-bold px-3 py-1.5 rounded-md bg-muted hover:bg-muted/70 text-foreground"
            data-testid="ipo-modal-dismiss-today"
          >
            Not today
          </button>
        </div>
      </div>
    </div>
  );
}
